import React, { useState, useRef, useCallback } from 'react';
import './DragDropImageUpload.css';

interface DragDropImageUploadProps {
  onFileSelect: (file: File) => void;
  previewUrl?: string;
  uploading?: boolean;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  placeholder?: string;
  className?: string;
  compact?: boolean;
}

export const DragDropImageUpload: React.FC<DragDropImageUploadProps> = ({
  onFileSelect,
  previewUrl,
  uploading = false,
  accept = 'image/png,image/jpeg,image/jpg,image/webp',
  maxSizeMB = 5,
  label,
  placeholder = 'Drop image here or click to browse',
  className = '',
  compact = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): boolean => {
    setError(null);
    
    // Check file type
    const allowedTypes = accept.split(',').map(t => t.trim());
    if (!allowedTypes.some(type => file.type === type || file.type.match(type.replace('*', '.*')))) {
      setError('Invalid file type. Please use PNG, JPG, or WebP.');
      return false;
    }
    
    // Check file size
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`File too large. Maximum size is ${maxSizeMB}MB.`);
      return false;
    }
    
    return true;
  };

  const handleFile = useCallback((file: File) => {
    if (validateFile(file)) {
      onFileSelect(file);
    }
  }, [onFileSelect, accept, maxSizeMB]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the container
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Trigger with empty to signal removal - parent should handle
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`drag-drop-upload ${className} ${compact ? 'compact' : ''}`}>
      {label && <span className="drag-drop-label">{label}</span>}
      
      <div
        className={`drag-drop-zone ${isDragging ? 'dragging' : ''} ${previewUrl ? 'has-preview' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="drag-drop-input"
        />
        
        {uploading ? (
          <div className="drag-drop-loading">
            <div className="drag-drop-spinner"></div>
            <span>Uploading...</span>
          </div>
        ) : previewUrl ? (
          <div className="drag-drop-preview">
            <img src={previewUrl} alt="Preview" />
            <div className="drag-drop-overlay">
              <span className="drag-drop-change">📷 Change Image</span>
              <span className="drag-drop-hint">Drop new image or click</span>
            </div>
          </div>
        ) : (
          <div className="drag-drop-placeholder">
            <div className="drag-drop-icon">
              {isDragging ? '📥' : '🖼️'}
            </div>
            <span className="drag-drop-text">
              {isDragging ? 'Drop image here' : placeholder}
            </span>
            <span className="drag-drop-subtext">
              PNG, JPG, WebP • Max {maxSizeMB}MB
            </span>
          </div>
        )}
      </div>
      
      {error && <span className="drag-drop-error">{error}</span>}
    </div>
  );
};

export default DragDropImageUpload;
